import type { Tag } from '@dnx/contracts';
import { Alert, Button, Card, Input, Spinner, cn } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { TAG_COLORS, tagsQuery } from '../reception/queries.js';
import { TagChips } from '../reception/tags.js';
import { SectionHeader } from './layout.js';

function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div className="flex gap-1.5" role="radiogroup" aria-label="Cor">
      {TAG_COLORS.map((c) => (
        <button key={c} type="button" role="radio" aria-checked={value === c} aria-label={`Cor ${c}`} onClick={() => onChange(c)}
          className={cn('grid size-7 place-items-center rounded-full ring-offset-2 ring-offset-surface', value === c && 'ring-2 ring-fg')}
          style={{ backgroundColor: c }}>
          {value === c && <Check className="size-3.5 text-white" />}
        </button>
      ))}
    </div>
  );
}

function TagRow({ tag }: { tag: Tag }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tag.name);
  const [color, setColor] = useState(tag.color);
  const [confirm, setConfirm] = useState(false);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['tags'] }); void qc.invalidateQueries({ queryKey: ['crm'] }); void qc.invalidateQueries({ queryKey: ['inbox'] }); };
  const save = useMutation({ mutationFn: () => api<Tag>(`/tags/${tag.id}`, { method: 'PATCH', body: { name, color } }), onSuccess: () => { setEditing(false); refresh(); } });
  const remove = useMutation({ mutationFn: () => api<void>(`/tags/${tag.id}`, { method: 'DELETE' }), onSuccess: refresh });
  const error = save.error ?? remove.error;
  return (
    <li className="px-5 py-3.5">
      {editing ? (
        <form className="flex flex-wrap items-center gap-3" onSubmit={(e) => { e.preventDefault(); if (name.trim()) save.mutate(); }}>
          <Input className="w-56" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} autoFocus aria-label="Nome da etiqueta" />
          <ColorPicker value={color} onChange={setColor} />
          <div className="ml-auto flex gap-1">
            <Button size="sm" type="submit" loading={save.isPending}>Salvar</Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setName(tag.name); setColor(tag.color); }}><X className="size-4" /></Button>
          </div>
        </form>
      ) : (
        <div className="flex items-center gap-3">
          <TagChips tags={[tag]} />
          <div className="ml-auto flex gap-1">
            {confirm ? (
              <>
                <span className="self-center text-[13px] text-fg-muted">Sai de todos os contatos.</span>
                <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>Apagar</Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>Manter</Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="ghost" onClick={() => setEditing(true)}><Pencil className="size-4" />Editar</Button>
                <Button size="sm" variant="ghost" className="text-danger" onClick={() => setConfirm(true)}><Trash2 className="size-4" />Apagar</Button>
              </>
            )}
          </div>
        </div>
      )}
      {error && <Alert tone="danger" className="mt-2">{error.message}</Alert>}
    </li>
  );
}

/** Configurações → Etiquetas: rótulos dos contatos (ex.: VIP, Retorno, Indicação), com cor. */
export function TagsSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery(tagsQuery);
  const [name, setName] = useState('');
  const [color, setColor] = useState(TAG_COLORS[0]!);
  const create = useMutation({
    mutationFn: () => api<Tag>('/tags', { method: 'POST', body: { name, color } }),
    onSuccess: () => { setName(''); void qc.invalidateQueries({ queryKey: ['tags'] }); },
  });
  return (
    <div>
      <SectionHeader title="Etiquetas" description="Marque contatos para achar e filtrar depois (no funil e nas conversas). As etiquetas acompanham a pessoa em todos os atendimentos." />
      <Card className="mt-6 p-5">
        <form className="flex flex-wrap items-center gap-3" onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate(); }}>
          <Input className="w-64" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nova etiqueta (ex.: VIP)" maxLength={40} aria-label="Nome da nova etiqueta" />
          <ColorPicker value={color} onChange={setColor} />
          <Button type="submit" className="ml-auto" loading={create.isPending} disabled={!name.trim()}><Plus className="size-4" />Criar etiqueta</Button>
        </form>
        {create.error && <Alert tone="danger" className="mt-3">{create.error.message}</Alert>}
      </Card>
      {isLoading ? <Spinner className="mt-6 size-5 text-fg-subtle" label="Carregando" /> : data?.length ? (
        <Card className="mt-4 overflow-hidden"><ul className="divide-y divide-border">{data.map((t) => <TagRow key={t.id} tag={t} />)}</ul></Card>
      ) : <p className="mt-6 text-sm text-fg-muted">Nenhuma etiqueta ainda.</p>}
    </div>
  );
}
